import { keyBy } from 'lodash';
import { useInteraction } from '@/common/hooks/useInteraction';
import { useSafeInteractionParams } from '@/common/hooks/useSafeInteractionParams';
import { interactionTypeRootPathName } from '@/helpers';
import type { CoreRequest } from '$api-clients/hooks';

type Interaction =
  CoreRequest<'/interactions/{interactionId}'>['Data']['interaction'];
type Representation = Extract<Interaction, { type: 'lease' }>['representation'];
type OfferExperience = Extract<
  Interaction,
  { type: 'listing' }
>['offerExperience'];

type SidebarLinkId =
  | 'cover_sheet'
  | 'cover_letter'
  | 'documents'
  | 'pre_qual'
  | 'proof_of_funds'
  | 'disclosures'
  | 'timeline'
  | 'commission_form'
  | 'offers';

interface SidebarLinkDefinition {
  id: SidebarLinkId;
  label: string;
  slug: string;
  basePath: 'txm' | 'payment';
  disabledWhenCoverSheetIncomplete: boolean;
  disabledWhenHasUnrepresentedClient: boolean;
  unrepresentedClientTooltip?: string;
  /** Analytics eventContainer for the unrepresented-client tooltip hover. */
  unrepresentedClientTooltipEventContainer?: string;
}

const coverSheetLink: SidebarLinkDefinition = {
  id: 'cover_sheet',
  label: 'Cover Sheet',
  slug: 'cover-sheet',
  basePath: 'txm',
  disabledWhenCoverSheetIncomplete: false,
  disabledWhenHasUnrepresentedClient: false,
};

const coverLetterLink: SidebarLinkDefinition = {
  id: 'cover_letter',
  label: 'Cover Letter',
  slug: 'cover-letter',
  basePath: 'txm',
  disabledWhenCoverSheetIncomplete: false,
  disabledWhenHasUnrepresentedClient: false,
};

const documentsLink: SidebarLinkDefinition = {
  id: 'documents',
  label: 'Documents',
  slug: 'documents',
  basePath: 'txm',
  disabledWhenCoverSheetIncomplete: true,
  disabledWhenHasUnrepresentedClient: false,
};

const preQualLink: SidebarLinkDefinition = {
  id: 'pre_qual',
  label: 'Pre Qual',
  slug: 'pre-qual',
  basePath: 'txm',
  disabledWhenCoverSheetIncomplete: false,
  disabledWhenHasUnrepresentedClient: false,
};

const proofOfFundsLink: SidebarLinkDefinition = {
  id: 'proof_of_funds',
  label: 'Proof of Funds',
  slug: 'proof-of-funds',
  basePath: 'txm',
  disabledWhenCoverSheetIncomplete: false,
  disabledWhenHasUnrepresentedClient: false,
};

const disclosuresLink: SidebarLinkDefinition = {
  id: 'disclosures',
  label: 'Signed Disclosures',
  slug: 'disclosures',
  basePath: 'txm',
  disabledWhenCoverSheetIncomplete: false,
  disabledWhenHasUnrepresentedClient: false,
};

const timelineLink: SidebarLinkDefinition = {
  id: 'timeline',
  label: 'Closing Timeline',
  slug: 'timeline',
  basePath: 'txm',
  disabledWhenCoverSheetIncomplete: true,
  disabledWhenHasUnrepresentedClient: true,
  unrepresentedClientTooltip: 'Manage the closing timeline on the Listing',
  unrepresentedClientTooltipEventContainer: 'closing_timeline',
};

const commissionFormLink: SidebarLinkDefinition = {
  id: 'commission_form',
  label: 'Commission Form',
  slug: 'commission-forms',
  basePath: 'payment',
  disabledWhenCoverSheetIncomplete: true,
  disabledWhenHasUnrepresentedClient: true,
  unrepresentedClientTooltip: 'Complete the commission form on the Listing',
  unrepresentedClientTooltipEventContainer: 'commission_form',
};

const offersLink: SidebarLinkDefinition = {
  id: 'offers',
  label: 'Offers Received',
  slug: 'offers',
  basePath: 'txm',
  disabledWhenCoverSheetIncomplete: true,
  disabledWhenHasUnrepresentedClient: false,
};

function getLinksForType(
  interactionType: string,
  leaseRepresentation: Representation | null,
  offerExperience: OfferExperience | null,
): SidebarLinkDefinition[] {
  const isOffer = interactionType === 'offer';
  const isLease = interactionType === 'lease';
  const isListing = interactionType === 'listing';
  const isTransaction = isOffer || isListing || isLease;
  const isTenantLease = isLease && leaseRepresentation === 'tenant';
  const isDualLease = isLease && leaseRepresentation === 'dual';

  return [
    coverSheetLink,
    ...(isOffer ? [coverLetterLink] : []),
    documentsLink,
    ...(offerExperience === 'lom' && isListing ? [offersLink] : []),
    ...(isOffer ? [preQualLink, proofOfFundsLink, disclosuresLink] : []),
    ...(isTenantLease || isDualLease ? [preQualLink, disclosuresLink] : []),
    ...(isTransaction ? [timelineLink, commissionFormLink] : []),
  ];
}

function buildPathname(
  linkDef: SidebarLinkDefinition,
  interactionType: string,
  interactionId: string,
): string {
  const rootPath = interactionTypeRootPathName(interactionType);
  return `/${linkDef.basePath}/${rootPath}/${interactionId}/${linkDef.slug}`;
}

export const useSidebarLinks = () => {
  const { interactionType, interactionId } = useSafeInteractionParams();

  const { data } = useInteraction(interactionId, interactionType);

  const offerExperience: OfferExperience | null =
    data && 'offerExperience' in data ? data.offerExperience : null;

  const linkDefs = getLinksForType(
    interactionType,
    data?.representation ?? null,
    offerExperience,
  );
  const links = linkDefs.map((def) => ({
    label: def.label,
    pathname: buildPathname(def, interactionType, interactionId),
    id: def.id,
    disabledWhenCoverSheetIncomplete: def.disabledWhenCoverSheetIncomplete,
    disabledWhenHasUnrepresentedClient: def.disabledWhenHasUnrepresentedClient,
    unrepresentedClientTooltip: def.unrepresentedClientTooltip,
    unrepresentedClientTooltipEventContainer:
      def.unrepresentedClientTooltipEventContainer,
  }));

  return {
    links: keyBy(links, 'id'),
  };
};
